// Shared vocabulary of Finance › Payroll (UI-2). Statuses come from the server;
// this file only turns them into the words an operator reads.
import { formatDate } from "../financeUtils";

export const PAYROLL_BASE = "/business/finance/payroll";
export const PAYMENTS_PATH = `${PAYROLL_BASE}/payments`;
export const HISTORY_PATH = `${PAYROLL_BASE}/history`;

export const periodStatus = (row) => String(row?.status || "").toLowerCase();

export const periodName = (row) => row?.name || dateRange(row);

const toDate = (value) => {
  const date = new Date(String(value).length <= 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Compact covered dates: "Sep 16 – 30, 2026", "Aug 28 – Sep 3, 2026", "Dec 28, 2026 – Jan 3, 2027". */
export function dateRange(row) {
  const start = row?.start_date ? toDate(row.start_date) : null;
  const end = row?.end_date ? toDate(row.end_date) : null;
  if (!start || !end) return "";
  const month = (d) => d.toLocaleDateString("en-PH", { month: "short" });
  if (start.getFullYear() !== end.getFullYear()) {
    return `${month(start)} ${start.getDate()}, ${start.getFullYear()} – ${month(end)} ${end.getDate()}, ${end.getFullYear()}`;
  }
  const endPart = start.getMonth() === end.getMonth() ? `${end.getDate()}` : `${month(end)} ${end.getDate()}`;
  return `${month(start)} ${start.getDate()} – ${endPart}, ${end.getFullYear()}`;
}

/** "Oct 5, 2026" */
export function shortDate(value) {
  const date = value ? toDate(value) : null;
  return date ? date.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" }) : "";
}

/** "Sep 16 – 30, 2026 · pay date Oct 5, 2026 · 3 payslips" (no count before HR calculates). */
export function periodFacts(row) {
  const count = Number(row?.payslip_count || 0);
  return [
    row?.payroll_schedule_name || null,
    dateRange(row),
    row?.pay_date ? `pay date ${shortDate(row.pay_date)}` : null,
    count ? `${count} payslip${count === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" · ");
}

/** Latest Finance decision on a period, in words: "Returned to HR by Ana on Oct 1, 2026: fix Juan's overtime". */
export function describeReview(review) {
  if (!review) return null;
  const who = review.reviewedByName ? ` by ${review.reviewedByName}` : "";
  const when = review.createdAt ? ` on ${shortDate(review.createdAt)}` : "";
  const what = review.action === "returned" ? "Returned to HR" : "Approved";
  const basis = review.action === "approved" ? approvalBasisText(review) : "";
  return `${what}${who}${when}${review.remarks ? `: ${review.remarks}` : ""}${basis ? ` — ${basis}` : ""}`;
}

/** "Calculated by Ana · sent to Finance by Ben", or one person for both. Empty when neither is known. */
export function preparedByText({ calculatedByName, submittedByName } = {}) {
  if (calculatedByName && submittedByName) {
    return calculatedByName === submittedByName
      ? `Calculated and sent to Finance by ${calculatedByName}`
      : `Calculated by ${calculatedByName} · sent to Finance by ${submittedByName}`;
  }
  if (calculatedByName) return `Calculated by ${calculatedByName}`;
  if (submittedByName) return `Sent to Finance by ${submittedByName}`;
  return "";
}

/**
 * On what basis an approval stood (APP-HR-101). An owner exception is never worded as an independent review, and
 * an approval made before the basis was recorded says so instead of claiming either.
 */
export function approvalBasisText(review) {
  if (review?.approvalBasis === "independent") {
    const prepared = preparedByText(review);
    return `Independent review${prepared ? ` (${prepared.charAt(0).toLowerCase()}${prepared.slice(1)})` : ""}`;
  }
  if (review?.approvalBasis === "owner_exception") {
    return `Owner exception, not an independent review. Reason: ${review.exceptionReason || "not recorded"}`;
  }
  return "";
}

const TOOK_PART = {
  calculated: "You calculated this payroll",
  submitted: "You sent this payroll to Finance",
  both: "You calculated this payroll and sent it to Finance",
};

/**
 * Where the viewer stands on approving a payroll that waits for Finance (APP-HR-101). The server decides; this only
 * turns its answer into what to show: whether Approve is offered, whether it is the owner exception, and why not.
 * A payroll with no `approval` block (an older server) behaves as before.
 */
export function approvalStanding(period) {
  const approval = period?.approval;
  if (!approval) return { mode: "approve", prepared: "", note: "" };
  const prepared = preparedByText(approval);
  const part = TOOK_PART[approval.participation] || "You took part in this payroll";
  switch (approval.state) {
    case "independent":
      return { mode: "approve", prepared, note: "" };
    case "owner_exception_available":
      return { mode: "approve_exception", prepared, note: `${part}, and nobody else in this business can approve payroll. As the owner you can approve it as an exception, with a reason.`, reasonMinLength: approval.reasonMinLength || 10 };
    case "owner_must_defer": {
      const others = Number(approval.otherReviewers || 0);
      return { mode: null, prepared, note: `${part}, so ${others === 1 ? "the other person who can approve payroll" : `one of the ${others} other people who can approve payroll`} has to review it. You can still return it to HR.` };
    }
    case "participant":
      return { mode: null, prepared, note: `${part}, so someone else has to approve it. You can still return it to HR.` };
    default:
      return { mode: null, prepared, note: "There is no record of who calculated or sent this payroll. Return it to HR to be recalculated and sent again." };
  }
}

/** Salaries of a released period that still need a handover or the employee's confirmation. */
export const openSalaryCount = (row) => Number(row?.payroll_open_count || 0);

/**
 * Where a period sits for Finance. Every live lane is a job; History is
 * context. Released payroll lives under To pay until it is settled.
 */
export function periodLane(row) {
  const status = periodStatus(row);
  if (status === "under_review") return "review";
  if (status === "released") return "pay";
  return "history";
}

/** One line of what is happening to a period Finance cannot act on. */
export function historyState(row) {
  const status = periodStatus(row);
  const lastReview = row?.reviews?.[0];
  if (status === "approved") return "Approved — waiting for HR to release payslips";
  if (status === "settled") return "Settled";
  if (status === "cancelled") return "Cancelled";
  if (lastReview?.action === "returned") return "Returned to HR for correction";
  return "Being prepared by HR";
}

export const isWithHr = (row) => ["draft", "collecting_inputs", "computed", "approved"].includes(periodStatus(row));

/** Salary payment states (server-derived; see salary-payment.service salaryPaymentState). */
export const SALARY_STATE = {
  not_released: { label: "Not released yet", tone: "neutral", order: 3 },
  ready_for_claim: { label: "Ready for cash claim", tone: "neutral", order: 0 },
  awaiting_employee: { label: "Waiting for employee", tone: "neutral", order: 1 },
  paid: { label: "Paid", tone: "success", order: 2 },
  // Net pay was ₱0: there is nothing to hand over, and nothing was.
  no_cash_due: { label: "No cash due", tone: "neutral", order: 2 },
  paid_confirmed_by_owner: { label: "Paid — confirmed by owner", tone: "success", order: 2 },
};

export const isPaidState = (state) => state === "paid" || state === "paid_confirmed_by_owner";

export function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDate(value);
  return date.toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** A request key per salary until its handover succeeds, so a retry after a lost response cannot pay twice. */
export function newHandoverKey(recordId) {
  const random = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `salary-${recordId}-${random}`.slice(0, 100);
}

/** Plain server message for a failed action; never a status code. */
export const actionError = (error, fallback) => error?.serverMessage || error?.response?.data?.message || fallback;
